import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { TOTPSecretInput } from '@/sections/asset/components/tabs/credentials/TOTPSecretInput';

interface IdentityMfaDialogProps {
  open: boolean;
  // The identity's LABEL — the ONLY identity field shown as text. Never the secret.
  identityLabel: string;
  // The identity's current totpSecret. Passed only into TOTPSecretInput (a
  // password-type input) — never interpolated into any label/aria/title.
  value: string;
  // Commits the new canonical base32 secret (or '' to clear) to the identity via
  // the SetManager's replaceIdentity. TOTPSecretInput fires this on every change.
  onChange: (secret: string) => void;
  onClose: () => void;
}

/**
 * Nested optional shadcn Dialog for configuring one identity's 2FA/TOTP secret.
 * It renders its OWN Radix Dialog so Escape closes only this dialog (never the
 * surrounding wizard/host Modal). DialogContent carries `z-[150]` (and the
 * overlay is lifted to `z-[150]` with a lightened scrim) so it layers above the
 * z-[100] host Modal — the exact nested-over-modal pattern proven in
 * CredentialConfigModal. The secret lives only in the identity (host formData)
 * and is committed live via `onChange`; Done just closes (nothing unsaved).
 *
 * Secrets hygiene: only `identityLabel` is interpolated into visible text; the
 * secret `value` is handed exclusively to TOTPSecretInput (a password input) and
 * never appears in any label, aria, title, or header. No useMemo/useCallback:
 * React Compiler handles memoization.
 */
export function IdentityMfaDialog({
  open,
  identityLabel,
  value,
  onChange,
  onClose,
}: IdentityMfaDialogProps) {
  // Bumped on Clear to remount TOTPSecretInput via its `key`, forcing it to
  // re-seed its internal draft from the now-empty `value`. TOTPSecretInput seeds
  // its draft once at mount and never resyncs on `value` change, so without this
  // remount a Clear leaves the stale secret visible (and a later keystroke would
  // re-commit it). Remount-on-Clear is scoped here and avoids a value-syncing
  // effect that would fight the operator's in-progress spaced input.
  const [clearNonce, setClearNonce] = useState(0);

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent
        className="z-[150]"
        overlayClassName="z-[150] bg-black/20 dark:bg-black/30"
        data-testid="identity-mfa-dialog"
      >
        <DialogHeader>
          <DialogTitle>Two-factor / TOTP (optional)</DialogTitle>
          <DialogDescription>
            {`Configuring MFA for "${identityLabel || 'Untitled'}". This secret belongs to this identity only.`}
          </DialogDescription>
        </DialogHeader>
        <TOTPSecretInput key={clearNonce} value={value} onChange={onChange} />
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              onChange('');
              setClearNonce(n => n + 1);
            }}
            data-testid="identity-mfa-clear"
          >
            Clear
          </Button>
          <Button onClick={onClose} data-testid="identity-mfa-done">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
